import React, { useState, useMemo } from 'react';

const STATUS_COLORS = {
  green: '#16a34a',
  amber: '#f59e0b',
  red: '#dc2626',
};

const STATUS_ORDER = { red: 0, amber: 1, green: 2 };

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '20px',
  },
  summaryBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '16px',
  },
  pillGreen: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 12px',
    borderRadius: '9999px',
    fontSize: '12px',
    fontWeight: 600,
    background: '#dcfce7',
    color: '#166534',
  },
  pillAmber: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 12px',
    borderRadius: '9999px',
    fontSize: '12px',
    fontWeight: 600,
    background: '#fef3c7',
    color: '#92400e',
  },
  pillRed: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 12px',
    borderRadius: '9999px',
    fontSize: '12px',
    fontWeight: 600,
    background: '#fee2e2',
    color: '#991b1b',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '12px',
    marginBottom: '16px',
  },
  toggleButton: {
    padding: '5px 14px',
    fontSize: '12px',
    fontWeight: 500,
    borderRadius: '9999px',
    border: '1px solid #d1d5db',
    background: '#fff',
    color: '#374151',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    lineHeight: 1.4,
  },
  toggleButtonActive: {
    background: '#1f2937',
    color: '#fff',
    borderColor: '#1f2937',
  },
  dotGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
  },
  dot: {
    width: '14px',
    height: '14px',
    borderRadius: '50%',
    cursor: 'pointer',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
    position: 'relative',
    flexShrink: 0,
  },
  dotHovered: {
    transform: 'scale(1.4)',
    boxShadow: '0 0 0 2px rgba(0,0,0,0.15)',
  },
  tooltip: {
    position: 'absolute',
    bottom: '22px',
    left: '50%',
    transform: 'translateX(-50%)',
    background: '#1f2937',
    color: '#fff',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: 500,
    whiteSpace: 'nowrap',
    pointerEvents: 'none',
    zIndex: 10,
    lineHeight: 1.4,
  },
  tooltipArrow: {
    position: 'absolute',
    top: '100%',
    left: '50%',
    transform: 'translateX(-50%)',
    width: 0,
    height: 0,
    borderLeft: '4px solid transparent',
    borderRight: '4px solid transparent',
    borderTop: '4px solid #1f2937',
  },
  stateGroupWrapper: {
    marginBottom: '16px',
  },
  stateLabel: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginBottom: '6px',
  },
  emptyState: {
    color: '#6b7280',
    textAlign: 'center',
    padding: '40px 0',
    fontSize: '14px',
  },
};

function Dot({ address, isHovered, onMouseEnter, onMouseLeave }) {
  const color = STATUS_COLORS[address.status] || '#6b7280';

  return (
    <div
      style={{
        ...styles.dot,
        backgroundColor: color,
        ...(isHovered ? styles.dotHovered : {}),
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {isHovered && (
        <div style={styles.tooltip}>
          {address.label}{address.state ? ` (${address.state})` : ''}
          <div style={styles.tooltipArrow} />
        </div>
      )}
    </div>
  );
}

export default function DotMatrixChart({ data }) {
  const [hoveredDot, setHoveredDot] = useState(null);
  const [groupByState, setGroupByState] = useState(false);

  const sortedAddresses = useMemo(() => {
    if (!data || !data.addresses) return [];
    return [...data.addresses].sort((a, b) => {
      const aOrder = STATUS_ORDER[a.status] != null ? STATUS_ORDER[a.status] : 3;
      const bOrder = STATUS_ORDER[b.status] != null ? STATUS_ORDER[b.status] : 3;
      if (aOrder !== bOrder) return aOrder - bOrder;
      const aState = (a.state || '').toLowerCase();
      const bState = (b.state || '').toLowerCase();
      if (aState < bState) return -1;
      if (aState > bState) return 1;
      const aLabel = (a.label || '').toLowerCase();
      const bLabel = (b.label || '').toLowerCase();
      if (aLabel < bLabel) return -1;
      if (aLabel > bLabel) return 1;
      return 0;
    });
  }, [data]);

  const stateGroups = useMemo(() => {
    if (!groupByState) return null;
    const groups = {};
    sortedAddresses.forEach((addr) => {
      const state = addr.state || 'Unknown';
      if (!groups[state]) groups[state] = [];
      groups[state].push(addr);
    });
    // Sort state keys alphabetically
    const sortedKeys = Object.keys(groups).sort();
    return sortedKeys.map((state) => ({
      state,
      addresses: groups[state].sort((a, b) => {
        const aOrder = STATUS_ORDER[a.status] != null ? STATUS_ORDER[a.status] : 3;
        const bOrder = STATUS_ORDER[b.status] != null ? STATUS_ORDER[b.status] : 3;
        return aOrder - bOrder;
      }),
    }));
  }, [sortedAddresses, groupByState]);

  if (!data || !data.addresses || data.addresses.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Portfolio Health Overview</div>
        <div style={styles.subtitle}>Each dot represents one tracked address</div>
        <div style={styles.emptyState}>No address data available</div>
      </div>
    );
  }

  const counts = data.counts || { green: 0, amber: 0, red: 0 };

  return (
    <div style={styles.card}>
      <div style={styles.title}>Portfolio Health Overview</div>
      <div style={styles.subtitle}>Each dot represents one tracked address</div>

      <div style={styles.summaryBar}>
        <span style={styles.pillGreen}>{counts.green} Green</span>
        <span style={styles.pillAmber}>{counts.amber} Amber</span>
        <span style={styles.pillRed}>{counts.red} Red</span>
      </div>

      <div style={styles.toolbar}>
        <button
          style={{
            ...styles.toggleButton,
            ...(groupByState ? styles.toggleButtonActive : {}),
          }}
          onClick={() => setGroupByState((prev) => !prev)}
        >
          {groupByState ? 'Grouped by State' : 'Group by State'}
        </button>
        <span style={{ fontSize: '12px', color: '#9ca3af' }}>
          {data.total || sortedAddresses.length} addresses
        </span>
      </div>

      {groupByState && stateGroups ? (
        stateGroups.map((group) => (
          <div key={group.state} style={styles.stateGroupWrapper}>
            <div style={styles.stateLabel}>{group.state}</div>
            <div style={styles.dotGrid}>
              {group.addresses.map((addr) => (
                <Dot
                  key={addr.addressId}
                  address={addr}
                  isHovered={hoveredDot === addr.addressId}
                  onMouseEnter={() => setHoveredDot(addr.addressId)}
                  onMouseLeave={() => setHoveredDot(null)}
                />
              ))}
            </div>
          </div>
        ))
      ) : (
        <div style={styles.dotGrid}>
          {sortedAddresses.map((addr) => (
            <Dot
              key={addr.addressId}
              address={addr}
              isHovered={hoveredDot === addr.addressId}
              onMouseEnter={() => setHoveredDot(addr.addressId)}
              onMouseLeave={() => setHoveredDot(null)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
